import { TrendingUp } from 'lucide-react'
import React, { useMemo } from 'react'
import { CROSSOVER_COLUMNS } from '../../config/columns'
import { CONFIG } from '../../config/constants'
import { useColumnVisibility } from '../../hooks/useColumnVisibility'
import { formatISTTime } from '../../lib/date-utils'
import {
    getCountColorClass,
    getShiftColorClass,
    getVarianceColorClass,
    STRIKE_LABELS,
    TABLE_CLASSES,
} from '../../lib/table-utils'
import { AnalysisMode, type AnalysisResult, type MarketData } from '../../types'
import { type ColumnConfig, ColumnSelector } from '../ColumnSelector'
import { ShiftChip } from '../chips/ShiftChip'
import { VarianceChip } from '../chips/VarianceChip'
import { DataChip } from '../ui/ghost-table/DataChip'
import { TableWrapper } from '../ui/ghost-table/TableWrapper'

interface CurrentCrossoverTableProps {
    marketData: MarketData | null
    analysis: AnalysisResult | null
    isLive: boolean
    mode: AnalysisMode
    historyData?: any[]
}

export const CurrentCrossoverTable: React.FC<CurrentCrossoverTableProps> = ({
    marketData,
    analysis,
    isLive,
    mode,
    historyData = [],
}) => {
    // Use centralized column visibility hook
    const defaultColumns: ColumnConfig[] = CROSSOVER_COLUMNS
    const [columns, toggleColumn, isVisible] = useColumnVisibility(
        CONFIG.UI.STORAGE_KEYS.CROSSOVER_COLUMNS,
        defaultColumns,
    )

    // Robust Data Processing
    const displayData = useMemo(() => {
        // 1. Try to build a Live Row if we have Live Analysis
        if (analysis && marketData && marketData.options) {
            try {
                // Helper to find option safely
                const findOption = (strike: number) => marketData.options.find((o: any) => o.strike === strike)

                const resOpt = findOption(analysis.resistanceStrike)
                const supOpt = findOption(analysis.supportStrike)

                const isVolumeMode = mode === AnalysisMode.VOLUME

                // Safe number extraction
                const getVal = (val: any) => (typeof val === 'number' && !isNaN(val) ? val : 0)

                const row = {
                    isLiveRow: true,
                    timestamp: new Date(marketData.timestamp || Date.now()).toISOString(),
                    rank: 1,

                    // LTPs
                    ceLtp: getVal(resOpt?.callPrice),
                    peLtp: getVal(supOpt?.putPrice),

                    // Analysis metrics
                    resistanceValue: isVolumeMode ? getVal(resOpt?.callVolume) : getVal(resOpt?.callOpenInterest),
                    resistanceStrike: analysis.resistanceStrike,
                    resistanceStrike2: analysis.resistanceStrike2,

                    supportValue: isVolumeMode ? getVal(supOpt?.putVolume) : getVal(supOpt?.putOpenInterest),
                    supportStrike: analysis.supportStrike,
                    supportStrike2: analysis.supportStrike2,

                    // Variance / Counts (Safe Defaults)
                    ceCount: analysis.variance?.ceCount ?? 0,
                    peCount: analysis.variance?.peCount ?? 0,
                    finalCount: analysis.variance?.totalCount ?? 0,

                    maxDiff: analysis.variance?.maxDiff ?? 0,
                    finalVariance: analysis.variance?.finalVariance ?? 0,

                    shift: analysis.shift,
                    shiftOld: analysis.shiftOld,
                    atmStrike: analysis.atmStrike,
                }
                return [row]
            } catch (err) {
                console.error('Error building live row:', err)
            }
        }

        // 2. Fallback: Use latest history item if available
        // CRITICAL FIX: Only fallback to history if we are NOT in live mode, OR if we genuinely have no live data yet.
        // We check if it's explicitly explicitly supposed to be history, or we wait.
        if (!isLive && historyData && historyData.length > 0) {
            const lastItem = historyData[historyData.length - 1]
            if (lastItem) return [lastItem]
        }

        // 3. No data
        return []
    }, [analysis, marketData, historyData, mode, isLive])

    // 🚀 PERF FIX: Removed useDeferredValue to prevent render starvation.
    // Because live data updates every 2 seconds, a deferred value gets continuously
    // interrupted and aborted by React, causing the UI to freeze and never show live data.
    const deferredData = displayData

    // Safety helper for rendering numbers
    const safeFixed = (val: any, digits: number = 2) => {
        return typeof val === 'number' && !isNaN(val) ? val.toFixed(digits) : '0.00'
    }

    // Use centralized IST time formatter from date-utils

    // INLINE COMPONENT HELPERS TO PREVENT RENDERING CRASHES
    // We duplicate the styling logic here to ensure it renders even if external components fail
    const renderNumberCell = (
        value: number | string,
        options: {
            className?: string
            decimals?: number
            showSign?: boolean
            suffix?: string
            isCurrency?: boolean
        } = {},
    ) => {
        let numVal = typeof value === 'number' ? value : parseFloat(value as string)
        if (isNaN(numVal)) numVal = 0

        const { className = '', decimals = 2, showSign = false, suffix = '' } = options

        let color: 'emerald' | 'rose' | 'slate' = 'slate'
        if (numVal > 0) color = 'emerald'
        if (numVal < 0) color = 'rose'

        // Override color based on classname to preserve specific semantics if needed
        if (className.includes('text-emerald') || className.includes('text-green')) color = 'emerald'
        if (className.includes('text-rose') || className.includes('text-red')) color = 'rose'

        const formatted = Math.abs(numVal).toFixed(decimals)
        const sign = showSign && numVal > 0 ? '+' : numVal < 0 ? '-' : ''

        return (
            <DataChip 
                value={`${sign}${formatted}`} 
                color={color} 
                suffix={suffix} 
                className={className} 
            />
        )
    }

    const renderStrikeChip = (
        strike: number,
        label: string | null,
        type: 'support' | 'resistance' | 'atm' | 'neutral' = 'neutral',
    ) => {
        const baseClasses =
            'relative w-24 h-8 flex items-center justify-center rounded-[10px] border text-[13px] font-bold tracking-tight shadow-sm transition-all mx-auto bg-white dark:bg-slate-800 dark:text-slate-200'
        let typeClasses = 'border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-400'
        let dotClasses = 'hidden'

        if (type === 'support') {
            typeClasses =
                'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
            dotClasses =
                'bg-white dark:bg-slate-900 border-2 border-white dark:border-slate-800 text-[9px] text-emerald-800 dark:text-emerald-400 shadow-sm flex'
        } else if (type === 'resistance') {
            typeClasses =
                'bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
            dotClasses =
                'bg-white dark:bg-slate-900 border-2 border-white dark:border-slate-800 text-[9px] text-rose-800 dark:text-rose-400 shadow-sm flex'
        } else if (type === 'atm') {
            typeClasses =
                'bg-white dark:bg-slate-800 border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 shadow-[0_0_0_4px_rgba(250,204,21,0.1)] dark:shadow-[0_0_0_4px_rgba(250,204,21,0.05)]'
            dotClasses =
                'bg-yellow-50 dark:bg-yellow-900/30 border-2 border-white dark:border-slate-800 text-[9px] text-yellow-800 dark:text-yellow-400 shadow-sm flex'
        }

        return (
            <div className={`${baseClasses} ${typeClasses}`}>
                {strike}
                {label && (
                    <div
                        className={`absolute -top-1.5 -right-1.5 w-[18px] h-[18px] rounded-full items-center justify-center font-black ${dotClasses}`}
                    >
                        {label}
                    </div>
                )}
            </div>
        )
    }

    const RenderTh = ({
        children,
        align = 'center',
        color,
        className = '',
    }: {
        children: React.ReactNode
        align?: 'left' | 'right' | 'center'
        color?: 'red' | 'green' | 'blue' | 'amber' | 'purple' | 'neutral'
        className?: string
    }) => {
        let bgClass = 'bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700'
        let textClass = 'text-slate-600 dark:text-slate-400 font-bold'

        if (color === 'green') {
            bgClass = 'bg-emerald-50 dark:bg-emerald-900/30 border-b-2 border-emerald-200 dark:border-emerald-800'
            textClass = 'text-emerald-700 dark:text-emerald-400 font-bold'
        } else if (color === 'red') {
            bgClass = 'bg-rose-50 dark:bg-rose-900/30 border-b-2 border-rose-200 dark:border-rose-800'
            textClass = 'text-rose-700 dark:text-rose-400 font-bold'
        } else if (color === 'amber') {
            bgClass = 'bg-amber-50 dark:bg-amber-900/30 border-b-2 border-amber-200 dark:border-amber-800'
            textClass = 'text-amber-700 dark:text-amber-400 font-bold'
        }

        const alignClass = align === 'left' ? 'text-left pl-6' : align === 'right' ? 'text-right pr-6' : 'text-center'

        return (
            <th
                className={`py-3 px-2 text-[11px] uppercase tracking-wider whitespace-nowrap sticky top-0 z-30 transition-colors duration-300 ${bgClass} ${textClass} ${alignClass} ${className}`}
            >
                {children}
            </th>
        )
    }

    return (
        <React.Fragment>
            <TableWrapper>
                {/* Header Section */}
                <div className="px-6 py-5 flex flex-wrap gap-y-4 justify-between items-center border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-t-[20px] transition-colors duration-300">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-indigo-100 dark:ring-indigo-800">
                            <TrendingUp size={22} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                Current Crossover Analysis
                            </h2>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-medium">
                                Real-time Support vs Resistance dynamics
                            </p>
                        </div>
                    </div>
                    <ColumnSelector columns={columns} onChange={toggleColumn} />
                </div>

                {/* Table Content */}
                <div className="w-full overflow-x-auto custom-scrollbar">
                    <table className={TABLE_CLASSES.TABLE}>
                        <thead className={TABLE_CLASSES.THEAD}>
                            <tr>
                                {isVisible('time') && (
                                    <RenderTh align="left" className="pl-6">
                                        Timestamp
                                    </RenderTh>
                                )}
                                {isVisible('rank') && <RenderTh align="center">Rank</RenderTh>}

                                {/* CE Side */}
                                {isVisible('ce_ltp') && (
                                    <RenderTh align="center" color="green">
                                        CE LTP
                                    </RenderTh>
                                )}
                                {isVisible('resistance_value') && (
                                    <RenderTh align="center" color="green">
                                        Res. OI/Vol
                                    </RenderTh>
                                )}
                                {isVisible('resistance_strike') && (
                                    <RenderTh align="center" color="red">
                                        Resistance
                                    </RenderTh>
                                )}
                                {isVisible('resistance_strike2') && (
                                    <RenderTh align="center" color="red">
                                        Res. 2
                                    </RenderTh>
                                )}

                                {/* Support Side */}
                                {isVisible('support_strike') && (
                                    <RenderTh align="center" color="green">
                                        Support
                                    </RenderTh>
                                )}
                                {isVisible('support_strike2') && (
                                    <RenderTh align="center" color="green">
                                        Sup. 2
                                    </RenderTh>
                                )}
                                {isVisible('support_value') && (
                                    <RenderTh align="center" color="red">
                                        Sup. OI/Vol
                                    </RenderTh>
                                )}
                                {isVisible('pe_ltp') && (
                                    <RenderTh align="center" color="red">
                                        PE LTP
                                    </RenderTh>
                                )}

                                {/* Counts */}
                                {isVisible('ce_count') && <RenderTh align="center">CE Count</RenderTh>}
                                {isVisible('pe_count') && <RenderTh align="center">PE Count</RenderTh>}
                                {isVisible('final_count') && (
                                    <RenderTh align="center" className="whitespace-normal w-20 leading-tight">
                                        Final Count
                                    </RenderTh>
                                )}

                                {/* Variance */}
                                {isVisible('max_oi_diff') && <RenderTh align="center">Max Diff</RenderTh>}
                                {isVisible('min_oi_diff') && <RenderTh align="center">Min Diff</RenderTh>}
                                {isVisible('variance') && <RenderTh align="center">Variance</RenderTh>}

                                {/* Other */}
                                {isVisible('shift') && <RenderTh align="center">Shift</RenderTh>}
                                {isVisible('shift_old') && <RenderTh align="center">Shift Old</RenderTh>}
                                {isVisible('atm_strike') && (
                                    <RenderTh align="center" color="amber">
                                        ATM
                                    </RenderTh>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50 premium-cell-mono text-xs bg-white/40 dark:bg-slate-900/40">
                            {deferredData.map((row, idx) => (
                                <tr key={idx} className={TABLE_CLASSES.TR}>
                                    {isVisible('time') && (
                                        <td className={`${TABLE_CLASSES.TD} pl-6`}>{formatISTTime(row.timestamp)}</td>
                                    )}
                                    {isVisible('rank') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            <span className="font-bold text-slate-700">#{row.rank}</span>
                                        </td>
                                    )}

                                    {/* CE Side */}
                                    {isVisible('ce_ltp') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.ceLtp, { className: 'text-emerald-700' })}
                                        </td>
                                    )}
                                    {isVisible('resistance_value') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.resistanceValue / 100000, {
                                                suffix: ' L',
                                                decimals: 2,
                                                className: 'text-slate-600',
                                            })}
                                        </td>
                                    )}
                                    {isVisible('resistance_strike') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderStrikeChip(row.resistanceStrike, null, 'resistance')}
                                        </td>
                                    )}
                                    {isVisible('resistance_strike2') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderStrikeChip(row.resistanceStrike2, null, 'resistance')}
                                        </td>
                                    )}

                                    {/* Support Side */}
                                    {isVisible('support_strike') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderStrikeChip(row.supportStrike, null, 'support')}
                                        </td>
                                    )}
                                    {isVisible('support_strike2') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderStrikeChip(row.supportStrike2, null, 'support')}
                                        </td>
                                    )}
                                    {isVisible('support_value') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.supportValue / 100000, {
                                                suffix: ' L',
                                                decimals: 2,
                                                className: 'text-slate-600',
                                            })}
                                        </td>
                                    )}
                                    {isVisible('pe_ltp') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.peLtp, { className: 'text-rose-700' })}
                                        </td>
                                    )}

                                    {/* Counts */}
                                    {isVisible('ce_count') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.ceCount, { showSign: true, decimals: 0 })}
                                        </td>
                                    )}
                                    {isVisible('pe_count') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.peCount, { showSign: true, decimals: 0 })}
                                        </td>
                                    )}
                                    {isVisible('final_count') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.finalCount, {
                                                showSign: true,
                                                decimals: 0,
                                                className: 'font-black text-sm',
                                            })}
                                        </td>
                                    )}

                                    {/* Variance */}
                                    {isVisible('max_oi_diff') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.maxDiff, {
                                                suffix: '%',
                                                decimals: 0,
                                                className: 'text-slate-500',
                                            })}
                                        </td>
                                    )}
                                    {isVisible('min_oi_diff') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderNumberCell(row.minDiff, {
                                                suffix: '%',
                                                decimals: 0,
                                                className: 'text-slate-500',
                                            })}
                                        </td>
                                    )}
                                    {isVisible('variance') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            <VarianceChip value={row.finalVariance || 0} />
                                        </td>
                                    )}

                                    {/* Other */}
                                    {isVisible('shift') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            <ShiftChip value={row.shift || 0} />
                                        </td>
                                    )}
                                    {isVisible('shift_old') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            <ShiftChip value={row.shiftOld || 0} />
                                        </td>
                                    )}
                                    {isVisible('atm_strike') && (
                                        <td className={`${TABLE_CLASSES.TD} text-center`}>
                                            {renderStrikeChip(row.atmStrike, 'ATM', 'atm')}
                                        </td>
                                    )}
                                </tr>
                            ))}

                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan={20} className="p-8 text-center text-slate-400 italic">
                                        Waiting for Live Data... <br />
                                        <span className="text-xs text-slate-300">
                                            (Market Data: {marketData ? 'Connected' : 'No Signal'}, Analysis:{' '}
                                            {analysis ? 'Ready' : 'Pending'})
                                        </span>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </TableWrapper>
        </React.Fragment>
    )
}
